/**
 * 报名转订单对话框（组级候选 + 真实下单）。
 *
 * 候选与差异来自报名提交组读模型；切换商品时要求强确认并记录结构化原因，
 * 确认后调用订单能力创建商品包订单。
 */
import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Input,
  message,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
} from "antd";
import { DraggableModal } from "../../components/DraggableModal";
import {
  enrollmentsApi,
  type SubmissionConvertCandidatesResult,
} from "../../api/enrollments.api";
import {
  billingApi,
  ORDER_SWITCH_REASON_OPTIONS,
  type OrderSwitchReasonCode,
} from "../../api/billing.api";
import { productApi, type ProductItem } from "../../api/products.api";

const fenToYuan = (fen: number): string => `¥${(fen / 100).toFixed(2)}`;

export interface ConvertOrderModalProps {
  open: boolean;
  /** 报名提交组 ID。 */
  submissionId: string;
  onClose: () => void;
  onCreated?: () => void;
}

export function ConvertOrderModal({
  open,
  submissionId,
  onClose,
  onCreated,
}: ConvertOrderModalProps) {
  const [result, setResult] =
    useState<SubmissionConvertCandidatesResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [productId, setProductId] = useState<string | undefined>();
  const [selectedProductDetail, setSelectedProductDetail] =
    useState<ProductItem | null>(null);
  const [switchConfirmed, setSwitchConfirmed] = useState(false);
  const [reasonCode, setReasonCode] = useState<
    OrderSwitchReasonCode | undefined
  >();
  const [reasonNote, setReasonNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setResult(null);
    setProductId(undefined);
    setSwitchConfirmed(false);
    setReasonCode(undefined);
    setReasonNote("");
    enrollmentsApi
      .listSubmissionConvertCandidates(submissionId)
      .then((candidateResult) => {
        setResult(candidateResult);
        setProductId(
          candidateResult.defaultProductId ??
            candidateResult.candidates[0]?.productId,
        );
      })
      .catch(() => setResult(null))
      .finally(() => setLoading(false));
  }, [open, submissionId]);

  useEffect(() => {
    if (!open || !productId) {
      setSelectedProductDetail(null);
      return;
    }
    let active = true;
    productApi
      .getProduct(productId)
      .then((productDetail) => {
        if (active) setSelectedProductDetail(productDetail);
      })
      .catch(() => {
        if (active) setSelectedProductDetail(null);
      });
    return () => {
      active = false;
    };
  }, [open, productId]);

  const selectedProduct = result?.candidates.find(
    (candidate) => candidate.productId === productId,
  );
  const skuNameOf = (skuId: string): string => {
    const application = result?.applications.find(
      (item) => item.skuId === skuId,
    );
    if (application) return application.skuName;
    const productSku = selectedProductDetail?.skuConfigs.find(
      (item) => item.skuId === skuId,
    );
    return productSku?.skuName ?? skuId.slice(0, 8);
  };
  const switched = Boolean(
    result?.sourceProductId &&
    selectedProduct &&
    selectedProduct.productId !== result.sourceProductId,
  );
  const canSubmit = Boolean(
    selectedProduct && (!switched || (switchConfirmed && reasonCode)),
  );
  const depositAmount = selectedProduct
    ? Math.round(selectedProduct.price * selectedProduct.depositRatio)
    : 0;
  const finalAmount = selectedProduct
    ? selectedProduct.price - depositAmount
    : 0;

  const submit = async (): Promise<void> => {
    if (!canSubmit || !result || !selectedProduct) return;
    setSubmitting(true);
    try {
      const created = await billingApi.createOrder({
        sourceSubmissionId: result.submissionId,
        studentId: result.studentId,
        campusId: result.campusId,
        productId: selectedProduct.productId,
        source: "STAFF_ASSISTED",
        switchConfirmed: switched ? true : undefined,
        switchReasonCode: switched ? (reasonCode ?? null) : null,
        switchReasonNote: switched && reasonNote ? reasonNote : null,
      });
      message.success(
        created.remainingUncoveredApplicationCount > 0
          ? `订单 ${created.orderNo} 已创建；仍有 ${created.remainingUncoveredApplicationCount} 条报名待跟进`
          : `订单 ${created.orderNo} 已创建`,
      );
      onCreated?.();
      onClose();
    } catch (error) {
      message.error(error instanceof Error ? error.message : "创建订单失败");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DraggableModal
      title="报名转订单"
      open={open}
      onCancel={onClose}
      width={680}
      footer={[
        <Button
          key="convert"
          type="primary"
          loading={submitting}
          disabled={!canSubmit}
          onClick={() => void submit()}
          data-testid="convert-submit-btn"
        >
          转订单
        </Button>,
      ]}
    >
      {result && (
        <p style={{ marginBottom: 12 }}>
          学生：<strong>{result.studentName}</strong> · 含
          <strong>{result.applications.length}</strong> 门课程
        </p>
      )}
      {loading && <Spin />}
      {!loading && !result && (
        <Alert type="error" showIcon message="转订单候选加载失败" />
      )}
      {!loading && result?.convertibility === "NO_CANDIDATE" && (
        <Alert
          type="warning"
          showIcon
          message="暂不可转订单"
          description="没有包含待转化课程 SKU 的在售商品；请先在课程商品管理中上架后重试。报名跟进不受影响。"
          data-testid="convert-no-candidate"
        />
      )}
      {result && result.sourceProductId && !result.defaultProductId && (
        <Alert
          style={{ marginBottom: 12 }}
          type="warning"
          showIcon
          message="来源商品当前不可选"
          description="请选择新的在售商品包，并确认下方课程差异。"
        />
      )}
      {!loading && result && result.candidates.length > 0 && (
        <>
          {result.applications.length > 0 && (
            <div
              style={{ marginBottom: 12 }}
              data-testid="convert-applications"
            >
              <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>
                课程明细
              </div>
              <Space wrap>
                {result.applications.map((application) => (
                  <Tag
                    key={application.applicationId}
                    color={application.covered ? "green" : "blue"}
                  >
                    {application.skuName}（赠送课余额 {application.giftedBalance}）
                    {application.covered ? " · 已覆盖" : " · 待转化"}
                  </Tag>
                ))}
              </Space>
            </div>
          )}
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>
              选择课程商品（默认选中来源商品，可切换）
            </div>
            <Select
              style={{ width: "100%" }}
              value={productId}
              onChange={(value) => {
                setProductId(value);
                setSwitchConfirmed(false);
                setReasonCode(undefined);
                setReasonNote("");
              }}
              options={result.candidates.map((candidate) => ({
                label: `${candidate.productName}（${fenToYuan(candidate.price)}）`,
                value: candidate.productId,
              }))}
              data-testid="convert-product-select"
            />
          </div>
          {selectedProduct && (
            <Space
              direction="vertical"
              style={{ marginBottom: 16 }}
              data-testid="convert-diff"
            >
              <span>
                应收总额：<strong>{fenToYuan(selectedProduct.price)}</strong>
              </span>
              <span>
                可选分期：首付款 <strong>{fenToYuan(depositAmount)}</strong>（
                {Math.round(selectedProduct.depositRatio * 100)}%），尾款{" "}
                <strong>{fenToYuan(finalAmount)}</strong>（首付款结清后{" "}
                {selectedProduct.finalDueDays} 天内交齐）
              </span>
              <span>
                <Tag color="green">继续覆盖</Tag>
                {selectedProduct.uncoveredSkuIds.map(skuNameOf).join("、") ||
                  "-"}
                （本次转化）
              </span>
              <span>
                <Tag color="gold">已被覆盖</Tag>
                {selectedProduct.coveredSkuIds.map(skuNameOf).join("、") || "-"}
              </span>
              <span>
                <Tag color="purple">新增购买</Tag>
                {selectedProduct.newSkuIds.map(skuNameOf).join("、") || "-"}
                （无报名记录，随包购买开通正式课时）
              </span>
            </Space>
          )}
          {switched && (
            <>
              <div style={{ marginBottom: 12 }}>
                <Checkbox
                  checked={switchConfirmed}
                  onChange={(event) => setSwitchConfirmed(event.target.checked)}
                  data-testid="convert-switch-confirm"
                >
                  我已确认切换后的课程差异
                </Checkbox>
              </div>
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>
                  切换原因（必选）
                </div>
                <Select
                  style={{ width: "100%" }}
                  value={reasonCode}
                  onChange={setReasonCode}
                  options={ORDER_SWITCH_REASON_OPTIONS}
                  data-testid="convert-switch-reason"
                />
              </div>
              <Input.TextArea
                rows={2}
                maxLength={200}
                value={reasonNote}
                onChange={(event) => setReasonNote(event.target.value)}
                placeholder="补充备注（可选）"
                data-testid="convert-switch-note"
              />
            </>
          )}
          <Typography.Paragraph
            type="secondary"
            style={{ fontSize: 12, marginTop: 16, marginBottom: 0 }}
          >
            商品包整包成交；一次只创建一笔订单，剩余报名继续跟进。
          </Typography.Paragraph>
        </>
      )}
    </DraggableModal>
  );
}
